import { actionAsksInChat, CARD_TO_CHAT } from '../panelFocus';
import { currentCoords } from '../currentLocation';

export const connectWebSocket = (url) => ({
    type: 'WS_CONNECT',
    payload: { url }
});
  
export const disconnectWebSocket = () => ({
    type: 'WS_DISCONNECT'
});

const sendMessage = (messageType, content) => ({
    type: 'WS_SEND',
    payload: {
        messageType,
        ...content
    }    
});

export const sendText = (actionType, inputMessage, stationId, sessionId, threadId) => {
    const coords = currentCoords();
    return sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:" + actionType,
        params: {
            ref: randomStr(),
            station: stationId,
            message: inputMessage,
            ...(threadId && { threadId }),
            ...(coords && { coords }),
        }
    });
};

// station-entry-params: the deep-link thread open — assist:thread with the
// ?entry value attached. The agent maps it through the station's config-declared
// entry_action (fail-closed: no config or a bad value → ordinary greeting open).
// card-continue-at-station: `bring` is the ?with= ref of a card to open the chat with.
// station-open-session: `open` is a token the agent minted; only the agent can read it.
export const sendOpenThread = (stationId, sessionId, entry, bring, open) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:thread",
        params: {
            ref: randomStr(),
            station: stationId,
            message: "",
            ...(entry && { entry }),
            ...(bring && { with: bring }),
            ...(open && { open }),
        }
    });

// mailbox: deliver the messages that just arrived for this station into its chat.
export const requestMailDelivery = (stationId, sessionId, threadId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: 'assist:deliver_mail',
        params: {
            ref: randomStr(),
            station: stationId,
            ...(threadId && { threadId }),
        }
    });

// Every chat viewer's card click goes through here, so this is where a click that leaves a
// question for the chat sends the user back to it (the drawer listens; see panelFocus.js).
export const sendAction = (actionSpec, stationId, sessionId, threadId) => {
    if (actionAsksInChat(actionSpec)) {
        try {
            window.dispatchEvent(new CustomEvent(CARD_TO_CHAT));
        } catch (e) { /* no CustomEvent — the drawer stays where it is */ }
    }
    return sendChatAction(actionSpec, stationId, sessionId, threadId);
};

const sendChatAction = (actionSpec, stationId, sessionId, threadId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:action",
        params: {
            ref: randomStr(),
            station: stationId,
            action_spec: JSON.stringify(actionSpec),
            ...(threadId && { threadId }),
        }
    });

// tile-card-actions: an action-bearing card clicked on a RE-INVOKE embed page
// (gallery tile / shared link). assist:action is thread-only — the agent drops
// thread-less clicks silently — so tiles translate the card's action_spec onto
// the qlink:invoke channel (!487): station-scoped, BFF-stamps subject (+
// server-resolved subject_name), guest-gated for anonymous. Mirrors
// SandboxedCard.invokeParams' wire shape; `prefilled` carries the args
// (e.g. Tobin claim's dish_id + prefilled name).
export const sendCardInvoke = (actionSpec, stationId, sessionId, inline = false) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: 'qlink:invoke',
        params: {
            ref: randomStr(),
            // The tile marker routes this through the BFF's in-process dispatch
            // rail (cross-station capable + ref-correlated reply); chat-page
            // invoke flows stay on their session-subscribed pipe semantics.
            tile: '1',
            // canvas-card (tile pass): an INLINE card — rendered by the page, a
            // `renderer:` card — needs the helper's emitted board back WHOLE on
            // this socket; the rail then answers with `cards` rather than a
            // stripped envelope and a toast. See ws.py _dispatch_tile_invoke.
            ...(inline ? { inline: '1' } : {}),
            // Cross-station actions: a search-result card's action targets ITS
            // restaurant (action_spec.station.id), not the page's aggregator
            // station — honor the spec's station like chat's inline-action
            // path does; the page station is only the fallback.
            station: String((actionSpec.station && actionSpec.station.id) || stationId),
            class: actionSpec.function_set || actionSpec.functionSet,
            function: actionSpec.function,
            act_params: JSON.stringify({ ...(actionSpec.prefilled || {}), aveq_id: '' }),
        },
    });

// station-chat-load-older: backfill older room history before `beforeId`
// (the oldest msgId currently on screen). The agent's broadcasting thread
// dispatches StationChat.load_older with the caller's identity injected and
// emits the page as room messages tagged older=True (the frontend prepends).
export const sendLoadOlder = (beforeId, stationId, sessionId, threadId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:load_older",
        params: {
            ref: randomStr(),
            station: stationId,
            before_id: beforeId,
            ...(threadId && { threadId }),
        }
    });

// A room member opens one of the room's live events from its link; the agent replies with its
// card, or that it has ended.
export const sendLiveOpen = (eventId, stationId, sessionId, threadId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:live",
        params: {
            ref: randomStr(),
            station: stationId,
            event: eventId,
            ...(threadId && { threadId }),
        }
    });

export const sendLiveEnd = (eventId, stationId, sessionId, threadId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:live_end",
        params: {
            ref: randomStr(),
            station: stationId,
            event: eventId,
            ...(threadId && { threadId }),
        }
    });

export const subscribeStation = (stationId) =>
    sendMessage('SUBSCRIBE_STATION', {
        stationId: stationId,
        timestamp: Date.now()
    });

// chat-helper-completion slug (#3): an ephemeral "is typing" ping. The BFF
// fans it to the station's other live browsers and does NOT forward it to
// qbric — it's transient presence, never persisted. Sent debounced from the
// chat textarea (see ChatInterface). No ref/response; fire-and-forget.
export const sendTyping = (stationId) =>
    sendMessage('TYPING', {
        stationId: stationId,
    });

// Resume an existing agent thread after the WS reconnects. The agent's
// ConversationLog is keyed by thread_id; if it still has the thread
// in-memory the prior conversation context is rehydrated and bound to
// the new qbric session. If it doesn't (agent restarted), the handler
// falls through to thread_handler so the user just gets a fresh thread.
// Either way, the browser sees a valid threadId on the response.
export const resumeThread = (stationId, sessionId, threadId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:resume_thread",
        params: {
            ref: randomStr(),
            station: stationId,
            thread_id: threadId,
        }
    });

// output-second-screen: the ?split gesture. The owner asks the BFF to mint a
// short-TTL share token for its current thread; the BFF replies SPLIT_TOKEN and
// the SPA renders the /s/<id>/<token> link/QR for a second screen.
export const sendSplit = (stationId, sessionId, threadId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:split",
        params: {
            ref: randomStr(),
            station: stationId,
            ...(threadId && { threadId }),
        }
    });

// output-second-screen: an output-only screen joins a thread read-only via a
// share token. The BFF redeems it (same-account + cap) and replies OBSERVING /
// OBSERVE_DENIED; thereafter the thread's OUTPUT frames mirror to this socket.
export const sendObserveThread = (token, stationId, sessionId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:observe_thread",
        params: {
            ref: randomStr(),
            station: stationId,
            token: token,
        }
    });

// output-second-screen: the stable token-less /s/<id>/view URL. The BFF resolves
// the viewer's own latest thread on the station (same-account) and observes it.
// kiosk-browser-display: `shared` drops the same-account filter server-side (the
// station's latest output from ANYONE) — for an unattended display account that
// never chats. The BFF gates it on station opt-in (config.secondary_display) +
// permission, so sending it speculatively is safe: worst case is OBSERVE_DENIED.
export const sendObserveStation = (stationId, sessionId, shared = false) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:observe_station",
        params: {
            ref: randomStr(),
            station: stationId,
            ...(shared ? { shared: true } : {}),
        }
    });

// The /embed/s/<station_id> (NUMERIC) ANONYMOUS observe. Same wire as
// sendObserveStation but with `embed: true` — the BFF gates it SOLELY on the
// station's config.embed_public (no token, no login). Read-only mirror.
//
// embeddable-card-live-action: this is now the INTERACTIVE / legacy path only.
// A NEW read-only embed is `/embed/s/<token>` and fetches /api/embed/s/<token>
// (re-invoke) instead of observing — see ChatInterface's embed bootstrap. The
// observe path stays because the interactive Suika embed (embeddable-card-
// interactive) reads through it (embed:true observe) alongside its guest-act.
export const sendObserveEmbed = (stationId, sessionId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:observe_station",
        params: {
            ref: randomStr(),
            station: stationId,
            embed: true,
        }
    });

// kiosk-send-split-link: pin THIS thread on the station's paired display(s).
// The BFF resolves the displays (config show_station == this station), mints
// an audience-bound share token server-side, and writes each display's
// `show_link` config prop — the browser never sees the token. Reply is a
// DISPLAY_PINNED frame (ok + displays[]/failed[]/reason).
export const sendSendToDisplay = (threadId, sessionId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:send_to_display",
        params: {
            ref: randomStr(),
            threadId: threadId,
        }
    });

// scheduled-output-screen slug: the token-less /s/<id>/scheduled URL — the
// viewer's OWN per-station scheduled-output board (a second screen of their
// scheduled-task cards). Same-account; the BFF gates on station permission and
// seeds from recent notification rows.
export const sendObserveScheduled = (stationId, sessionId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: "assist:observe_scheduled",
        params: {
            ref: randomStr(),
            station: stationId,
        }
    });

// station-chat-web-push: invoke a GLOBAL namespace action (e.g. webpush:subscribe
// / webpush:unsubscribe). Same ACTION frame as the assist:* sends but with an
// arbitrary actionUri and no station thread — the BFF forwards by the actionUri's
// namespace to the global service (the webpush-worker), stamping the account from
// the session. Fire-and-forget; no response is awaited.
export const sendGlobalAction = (actionUri, params, sessionId) =>
    sendMessage('ACTION', {
        sessionId: sessionId,
        actionUri: actionUri,
        params: {
            ref: randomStr(),
            ...(params || {}),
        }
    });

const randomStr = () => Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)